import { onCLS } from "web-vitals/attribution";

import { version } from "../version";
import { remove as removeCookie, setIfChanged } from "./cookies";
import { getEnvironment } from "./env";

const roundMetric = (value) => {
  // oxlint-disable-next-line anti-slop/no-runtime-typeof -- This JavaScript data boundary requires a primitive number; coercion or finiteness checks change NaN/Infinity semantics.
  if (typeof value !== "number" || Number.isNaN(value)) {
    return null;
  }

  return Math.round(value * 1000) / 1000;
};

const rectProperties = (prefix, rect) => {
  if (!rect) {
    return {};
  }

  return {
    [`${prefix}_top`]: roundMetric(rect.top),
    [`${prefix}_right`]: roundMetric(rect.right),
    [`${prefix}_bottom`]: roundMetric(rect.bottom),
    [`${prefix}_left`]: roundMetric(rect.left),
    [`${prefix}_width`]: roundMetric(rect.width),
    [`${prefix}_height`]: roundMetric(rect.height),
  };
};

const clearDistinctIdCookie = () => {
  removeCookie("mtn-posthog-distinct-id");
};

const now = () => Math.round(window.performance?.now?.() || 0);

const ensureArrayAt = () => {
  if (Array.prototype.at) {
    return;
  }

  // oxlint-disable-next-line no-extend-native -- The third-party SDK calls Array.at internally; its legacy-browser fallback must exist before SDK initialization.
  Object.defineProperty(Array.prototype, "at", {
    configurable: true,
    enumerable: false,
    value(index) {
      const length = Number(this.length) || 0;

      if (length === 0) {
        return;
      }

      const relativeIndex = Math.trunc(Number(index) || 0);

      const targetIndex =
        relativeIndex >= 0 ? relativeIndex : length + relativeIndex;

      if (targetIndex < 0 || targetIndex >= length) {
        return;
      }

      return this[targetIndex];
    },
    writable: true,
  });
};

const cleanMarkerValue = (value) => {
  if (
    // oxlint-disable-next-line anti-slop/no-runtime-typeof -- This JavaScript URL/DOM/SDK boundary requires a primitive string; coercion and boxed strings change accepted inputs.
    typeof value === "string" ||
    // oxlint-disable-next-line anti-slop/no-runtime-typeof -- This JavaScript data boundary requires a primitive number; coercion or finiteness checks change NaN/Infinity semantics.
    typeof value === "number" ||
    // oxlint-disable-next-line anti-slop/no-runtime-typeof -- This JavaScript consent boundary accepts primitive booleans only, without truthy coercion.
    typeof value === "boolean" ||
    value === null
  ) {
    return value;
  }
};

const clearMemberfulOrderUuid = () => {
  Reflect.deleteProperty(window, "mtnMemberfulOrderUuid");
};

// Exception types and message substrings confirmed as third-party / browser
// noise: zero stack frames and no actionable signal in our code.
const NOISE_EXCEPTION_TYPES = new Set([
  "HTMLScriptElement",
  "HTMLLinkElement",
  "CustomEvent",
  "Event",
]);

const CHUNK_EXCEPTION_TYPES = new Set(["TypeError", "Error"]);

const NOISE_MESSAGE_SUBSTRINGS = [
  "ResizeObserver loop completed",
  "Object Not Found Matching Id",
  "runtime.sendMessage",
  "Script error.",
  // Brave Shields cosmetic-filtering content script (MTN-1238).
  "standardSelectors",
  // Cross-realm browser-extension search widget (MTN-1238).
  "showSearchResults",
  // iOS Safari extension content script (MTN-1310).
  "contentScriptData",
  // Android WebView bridge in in-app browsers (Facebook, for example): the
  // host app tears down the WebView while its injected @JavascriptInterface
  // handler is still running (MTN-1310).
  "Java object is gone",
  // iOS/macOS counterpart: WKWebView's injected postMessage handler outlives
  // the host app's API client during teardown (MTN-1310).
  "WKWebView API client did not respond",
  // Third message variant of the same WKWebView teardown (MTN-1350).
  "WKWebView was deallocated",
];

// Stack frames that are by construction never first-party code. WebKit
// reports scripts it can't attribute to the page as
// "webkit-masked-url://hidden/" (MTN-1310); Safari can also hand over an
// extension's real on-disk file:// path (for example PayPal Honey's
// Extension.appex bundle), and other browsers use their extension schemes
// (MTN-1350). The server fingerprints on minified frame offsets, so each
// extension release mints a fresh issue.
const NON_PAGE_FRAME_PREFIXES = [
  "webkit-masked-url://",
  // Safe to treat as non-page even though it isn't an extension scheme:
  // first-party MTN pages never use file://. beforeSend also drops cookieless
  // file contexts because their payload has no $host.
  "file://",
  "safari-extension://",
  "safari-web-extension://",
  "chrome-extension://",
  "moz-extension://",
];

// Runtime-internal frames that attribute to no script; they must not veto the
// non-page verdict (extension async rejections interleave them, MTN-1350) and
// must not establish it either. Missing and empty filenames are likewise
// neutral, via the truthiness check in the loop below.
const NEUTRAL_FRAME_FILENAMES = new Set(["[native code]", "<anonymous>"]);

// True when the stack has at least one non-page frame and every other
// frame is neutral. Keep a mixed stack that has any attributable frame;
// that could be first-party code.
const hasOnlyNonPageFrames = (exception) => {
  const frames = exception.stacktrace?.frames;

  if (!frames?.length) {
    return false;
  }

  let sawNonPage = false;

  for (const { filename } of frames) {
    if (NON_PAGE_FRAME_PREFIXES.some((p) => filename?.startsWith(p))) {
      sawNonPage = true;
    } else if (filename && !NEUTRAL_FRAME_FILENAMES.has(filename)) {
      return false;
    }
  }

  return sawNonPage;
};

// Frame-less ObjectCoercer-wrapped messages confirmed as extension /
// third-party noise (MTN-1297, MTN-1310). Exact inner-message match gated on
// the frame-less synthetic wrapper shape below, so a stack-bearing application
// error with the same text still captures.
const NOISE_COERCED_MESSAGES = new Set([
  // Safari 26 web-vitals@5.3.0 interaction-cleanup crash (MTN-1297);
  // hasOnlyNonPageFrames covers the stack-bearing native shape of the same
  // crash, whose frames are all webkit-masked.
  "null is not an object (evaluating 'o.id')",
  // Frame-less twins of webkit-masked Safari extension errors (MTN-1310).
  "operation timed out",
  "Cannot call a class as a function",
  "Can't find variable: observer",
  "Message Timeout",
  // Brave Shields cosmetic-filtering content script (MTN-1310).
  "undefined is not an object (evaluating 't[r]')",
]);

// Browser-native messages for an aborted/failed fetch network request: tab
// closing mid-flight, ad-blocker / antivirus interception, captive portal,
// CSP violation, etc. Each browser uses a fixed message, so we match exactly
// to avoid catching application-level messages that happen to begin with
// "Failed to fetch" (an app-thrown Error whose message starts that way, say).
//
// Tradeoff: real operational regressions (CORS misconfig, origin outage,
// endpoint deletion) at fetch call sites also surface with these messages, and
// this filter suppresses them. Net win because user-side noise dominates by
// orders of magnitude; if we need to investigate a real outage, server-side
// signals (Cloudflare, Pagely, PostHog server-side) are the right source.
const NOISE_NETWORK_TYPEERROR_MESSAGES = new Set([
  // Chrome, Edge, Samsung Internet, Opera
  "Failed to fetch",
  // Safari
  "Load failed",
  // Firefox
  "NetworkError when attempting to fetch resource.",
  // iOS WKWebView / older Safari (tab close, back-nav)
  "cancelled",
]);

// Recommender Errors from util/recommender.js for edge-bot / rate-limit
// statuses — symmetric to SUPPRESSED_NON_JSON_STATUSES in util/fetch.js.
// Other 4xx and all 5xx still surface so real outages remain visible.
const NOISE_MTN_API_ERROR_MESSAGES = new Set([
  "MTN API error: 401",
  "MTN API error: 403",
  "MTN API error: 429",
]);

// posthog-js ObjectCoercer wraps non-standard/cross-realm Error-like values in
// two quoted fields. PostHog server fingerprint v2 masks both quoted fields,
// collapsing unrelated frame-less errors into one issue (MTN-956).
const OBJECT_COERCER_FINGERPRINT_PREFIX = "mtn-object-coercer-v1:";

const OBJECT_COERCER_VALUE_PATTERN =
  /^'(?<type>[\s\S]*)' captured as exception with message: '(?<message>[\s\S]*)'$/u;

const getObjectCoercerException = (value) => {
  const match = value.match(OBJECT_COERCER_VALUE_PATTERN);

  return match ? { message: match[2], type: match[1] } : null;
};

// The shape posthog-js produces from window.onerror when the browser reports
// an error it can't attribute to any script.
const isFramelessSynthetic = (exception) =>
  exception.mechanism?.synthetic === true &&
  exception.mechanism?.type === "generic" &&
  !exception.stacktrace?.frames?.length;

// Browser-level messages with no stack to act on, mapped to their exception
// type. Exact match gated on the frame-less synthetic shape below, so a
// stack-bearing error with the same message still captures.
const NOISE_FRAMELESS_MESSAGES = new Map([
  // Firefox allocation failure (MTN-1350).
  ["out of memory", "Error"],
  // Firefox raises this only for add-on code that touches a DOM object from a
  // destroyed document. One extension fired it on timers in every open MTN
  // tab, as often as every 72 seconds (MTN-1813).
  ["can't access dead object", "TypeError"],
]);

const isObjectCoercerFingerprintCollision = (exception, type, value) =>
  type === "Error" &&
  isFramelessSynthetic(exception) &&
  OBJECT_COERCER_VALUE_PATTERN.test(value);

// Automated/headless clients: crawlers and preview renderers (syndication
// partners re-rendering our slideshows, for example) that drive real Chrome
// under automation.
// They present a clean UA, so PostHog's UA-based bot filter misses them, but
// they paint ~16s late under CPU starvation and wrecked $web_vitals percentiles
// on low-traffic pages — three slideshow URLs read p75 LCP 21–25s that was 100%
// this cohort, while real users on the same pages saw <1s (MTN-953).
// navigator.webdriver is the WebDriver-spec automation flag: false for real
// users, true for Puppeteer/Playwright/Selenium/CDP-driven Chrome — so dropping
// on it has no real-user false positives (`=== true` also no-ops when the
// property is undefined on older browsers). This also drops our own headless
// smoke / critical-CSS runs, which is desirable. Caveat: it drops local
// capture-pipeline testing via headless Puppeteer too — exercise capture in dev
// from a non-automated browser.
const isAutomatedClient = () =>
  typeof navigator !== "undefined" && navigator.webdriver === true;

// posthog.test.js checks this list against the SDK's own CAMPAIGN_PARAMS.
const CHANNEL_TAGS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gad_source",
  "mc_cid",
  "gclid",
  "gclsrc",
  "dclid",
  "gbraid",
  "wbraid",
  "fbclid",
  "msclkid",
  "twclid",
  "li_fat_id",
  "igshid",
  "ttclid",
  "rdt_cid",
  "epik",
  "qclid",
  "sccid",
  "oppref",
  "irclid",
  "_kx",
];

// A PostHog session can start during site navigation. Its new session-entry
// referrer then points back to MTN, which PostHog classifies as Referral unless
// we mark that untagged self-referral as direct (MTN-1397).
const normalizeSelfReferral = (properties) => {
  const currentHost = properties?.$host;

  if (
    !currentHost ||
    properties.$session_entry_referring_domain !== currentHost ||
    CHANNEL_TAGS.some((tag) => properties[`$session_entry_${tag}`])
  ) {
    return;
  }

  if (properties.$referring_domain === currentHost) {
    properties.$referrer = "$direct";
    properties.$referring_domain = "$direct";
  }

  properties.$session_entry_referrer = "$direct";
  properties.$session_entry_referring_domain = "$direct";
};

const shouldCaptureWebVitals = (sessionId) => {
  if (!sessionId) {
    return false;
  }

  let bucket = 7;

  // Keep the existing first UTF-16 code-unit hash, including astral input.
  for (const character of sessionId) {
    bucket = (bucket * 37 + character[0].codePointAt(0)) % 10;
  }

  return bucket === 0;
};

const shouldDropPreCaptureEvent = (event) => {
  const properties = event?.properties;

  // Cookieless identification hashes $host + IP + UA, so an absent host would
  // assign traffic to the wrong site (MTN-1310).
  if (properties?.$cookieless_mode && !properties.$host) {
    return true;
  }

  return (
    event?.event === "$web_vitals" &&
    !shouldCaptureWebVitals(properties?.$session_id)
  );
};

const addDefaultEventProperties = (properties) => {
  if (!properties) {
    return;
  }

  if (properties.environment === undefined) {
    properties.environment = getEnvironment();
  }

  if (properties.release === undefined) {
    properties.release = `mtn@${version}`;
  }
};

const isNoiseException = (ex, type, value, objectCoercerException) =>
  NOISE_EXCEPTION_TYPES.has(type) ||
  NOISE_MESSAGE_SUBSTRINGS.some((s) => value.includes(s)) ||
  hasOnlyNonPageFrames(ex) ||
  (isFramelessSynthetic(ex) && NOISE_FRAMELESS_MESSAGES.get(value) === type) ||
  (type === "TypeError" && NOISE_NETWORK_TYPEERROR_MESSAGES.has(value)) ||
  (objectCoercerException?.type === "TypeError" &&
    NOISE_NETWORK_TYPEERROR_MESSAGES.has(objectCoercerException.message)) ||
  (isObjectCoercerFingerprintCollision(ex, type, value) &&
    NOISE_COERCED_MESSAGES.has(objectCoercerException.message)) ||
  (type === "Error" && NOISE_MTN_API_ERROR_MESSAGES.has(value));

const MEMBERFUL_ORDER_UUID_PATTERN =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/u;

export const createPosthogBridge = (
  posthog,
  { CATEGORIES, onUnavailable, watchCategory },
  shouldDropChunkException
) => {
  // Exported for unit testing (posthog.test.js); not part of the module's public
  // API. Wired into posthog.init() below as `before_send`.
  const beforeSend = (event) => {
    // The SDK sends its initial pageview before loaded() can register super
    // properties. Add defaults here so automatic events receive the same tags
    // without a second, manual pageview.
    addDefaultEventProperties(event?.properties);

    if (isAutomatedClient()) {
      return null;
    }

    if (shouldDropPreCaptureEvent(event)) {
      return null;
    }

    normalizeSelfReferral(event?.properties);

    if (event?.event !== "$exception") {
      return event;
    }

    // PostHog parses UA into $browser/$os but strips the raw string, so in-app
    // WebViews (Facebook, Instagram, TikTok) are indistinguishable from Mobile
    // Safari at triage time. Tag exceptions only — keeps the cost off pageviews.
    if (event.properties) {
      event.properties.mtn_raw_user_agent = navigator.userAgent;
    }

    const ex = event.properties?.$exception_list?.[0];

    if (!ex) {
      return event;
    }

    const type = ex.type ?? ex.$exception_type ?? "";
    const value = ex.value ?? ex.$exception_value ?? "";
    const objectCoercerException = getObjectCoercerException(value);

    // Drop chunk-reload duplicates. The handler in util/chunk-reload.js arms a
    // flag when it triggers a reload; parallel or later dynamic-import failures
    // in the same page load are duplicates with no diagnostic value, and the
    // first event on a post-reload page passes through as the kill metric.
    // posthog-js v1.372 captures via `window.onunhandledrejection` (property
    // assignment), so this is the only layer that can suppress the duplicates —
    // see MTN-366. Browser chunk fetches are TypeErrors; Parcel's synchronous
    // missing-module runtime throw is an Error (MTN-1201).
    if (CHUNK_EXCEPTION_TYPES.has(type) && shouldDropChunkException(value)) {
      return null;
    }

    if (isNoiseException(ex, type, value, objectCoercerException)) {
      return null;
    }

    if (
      !Object.getOwnPropertyDescriptor(
        event.properties,
        "$exception_fingerprint"
      ) &&
      isObjectCoercerFingerprintCollision(ex, type, value)
    ) {
      event.properties.$exception_fingerprint = `${OBJECT_COERCER_FINGERPRINT_PREFIX}${type}:${value}`;
    }

    return event;
  };

  // Mirrors PostHog's distinct_id into an mtn-* cookie so Tune-Out can attach it
  // to affiliate clicks, letting Tune-Postback fire conversions with the same
  // distinct_id. Called only after Termly confirms analytics consent.

  const syncDistinctIdCookie = () => {
    const distinctId = posthog.get_distinct_id();

    if (!distinctId) {
      return;
    }

    // Host-only since MTN-321: the sole consumer, the Tune-Out Worker at
    // /out/*, reads it on the page's own host. A pre-MTN-321 parent-domain
    // copy keeps satisfying setIfChanged (and Tune-Out) until it ages out,
    // then the next load writes host-only. setIfChanged keeps a page load
    // alone from renewing the 91-day retention — the cookie refreshes only
    // when PostHog rotates the distinct ID or after the mirror itself expires.
    setIfChanged("mtn-posthog-distinct-id", distinctId, { days: 91 });
  };

  const CLS_DIAGNOSTIC_THRESHOLD = 0.1;
  const CLS_DIAGNOSTIC_MIN_DELTA = 0.05;
  const CLS_DIAGNOSTIC_LATE_SHIFT_MS = 5000;
  const CLS_DIAGNOSTIC_MUTATION_WINDOW_MS = 15_000;
  const CLS_DIAGNOSTIC_MUTATION_LIMIT = 20;

  const clsMutationMarks = [];
  let clsDiagnosticReports = 0;
  let clsDiagnosticLastValue = 0;
  let clsDiagnosticReportedHidden = false;

  // Tag every cookieless-mode entry path so the cookieless/cookied mix is
  // diagnosable in PostHog: a HogQL group-by on
  // `properties.mtn_opt_out_reason` for cookieless distinct_ids reveals which
  // path dominates (timer firing, real load failure, or genuine user reject).
  // posthog.register() writes to in-memory persistence, so the property rides
  // every event the SDK fires after the call — including the cookieless
  // _captureInitialPageview() that opt_out_capturing triggers.
  const optOutWithReason = (reason) => {
    clearDistinctIdCookie();
    posthog.set_config({ advanced_disable_feature_flags: true });
    posthog.register({ mtn_opt_out_reason: reason });
    posthog.opt_out_capturing({ captureEventName: null });
  };

  const recentClsMutations = () => {
    const pageAge = now();

    return clsMutationMarks.filter(
      (mark) => pageAge - mark.page_age_ms <= CLS_DIAGNOSTIC_MUTATION_WINDOW_MS
    );
  };

  const shouldCaptureClsDiagnostic = (metric) => {
    if (metric.value < CLS_DIAGNOSTIC_THRESHOLD) {
      return false;
    }

    const pageIsHidden = document.visibilityState === "hidden";

    if (pageIsHidden && !clsDiagnosticReportedHidden) {
      clsDiagnosticReportedHidden = true;

      return true;
    }

    if (clsDiagnosticReports === 0) {
      return true;
    }

    return metric.value - clsDiagnosticLastValue >= CLS_DIAGNOSTIC_MIN_DELTA;
  };

  // Pre-consent capture buffer (MTN-620). With cookieless_mode "on_reject" the
  // SDK's is_capturing() is false while explicit consent is pending, so every
  // capture fired before Termly resolves — early exceptions, CLS diagnostics,
  // newsletter_subscribed on /thank-you/ for first-time visitors — was silently
  // dropped. (chunk_reload_triggered stays lost in the pending window: its
  // caller reloads the page 50ms after capturing, long before any drain.)
  // The capture family buffers those calls and drains the buffer exactly once
  // when consent resolves:
  //   grant → flush cookied; explicit reject → discard (parity with the SDK's
  //   own drop-on-reject); CMP-failure fallback → flush cookieless, matching
  //   the anonymous initial-pageview replay the SDK already does there.
  // A single drain means a late Termly grant after the fallback can't
  // double-fire buffered events. Null marks the buffer as drained.
  let pendingCaptures = [];

  let initialized = false;

  const runOrBuffer = (fn) => {
    // Visitors whose SDK consent is already stored (returning grant, returning
    // reject → cookieless, DNT) capture immediately, exactly as before.
    if (pendingCaptures && !(initialized && posthog.is_capturing())) {
      pendingCaptures.push(fn);

      return;
    }

    fn();
  };

  const capture = (eventName, properties, options) =>
    runOrBuffer(() => posthog.capture(eventName, properties, options));

  const captureClsDiagnostic = (metric) => {
    if (!shouldCaptureClsDiagnostic(metric)) {
      return;
    }

    clsDiagnosticReports += 1;
    clsDiagnosticLastValue = metric.value;

    const attribution = metric.attribution || {};
    const source = attribution.largestShiftSource || {};
    const recentMutations = recentClsMutations();
    const [latestMutation] = recentMutations.slice(-1);
    const { largestShiftTime } = attribution;

    capture(
      "cls_diagnostic_recorded",
      {
        had_recent_adsense: recentMutations.some((mark) =>
          mark.type.startsWith("adsense_")
        ),
        had_recent_auto_block: recentMutations.some(
          (mark) => mark.type === "auto_block_populated"
        ),
        had_recent_smartasset_iframe: recentMutations.some(
          (mark) => mark.type === "smartasset_iframe_inserted"
        ),
        is_late_shift:
          // oxlint-disable-next-line anti-slop/no-runtime-typeof -- This JavaScript data boundary requires a primitive number; coercion or finiteness checks change NaN/Infinity semantics.
          (typeof largestShiftTime === "number" &&
            largestShiftTime >= CLS_DIAGNOSTIC_LATE_SHIFT_MS) ||
          attribution.loadState === "complete",
        largest_shift_load_state: attribution.loadState,
        largest_shift_target: attribution.largestShiftTarget,
        largest_shift_time_ms: roundMetric(largestShiftTime),
        largest_shift_value: roundMetric(attribution.largestShiftValue),
        latest_mutation_age_ms: latestMutation
          ? now() - latestMutation.page_age_ms
          : null,
        latest_mutation_type: latestMutation?.type,
        metric_delta: roundMetric(metric.delta),
        metric_entries_count: metric.entries?.length || 0,
        metric_id: metric.id,
        metric_rating: metric.rating,
        metric_value: roundMetric(metric.value),
        mtn_ads_enabled: !!window.mtnAdsEnabled,
        page_age_ms: now(),
        page_visibility_state: document.visibilityState,
        path: window.location.pathname,
        recent_mutation_count: recentMutations.length,
        recent_mutation_types: recentMutations.map((mark) => mark.type),
        recent_mutations: recentMutations.slice(-6),
        scroll_y: Math.round(window.scrollY),
        shift_source_node_name: source.node?.nodeName,
        url: `${window.location.origin}${window.location.pathname}`,
        viewport_height: window.innerHeight,
        viewport_width: window.innerWidth,
        ...rectProperties("source_previous", source.previousRect),
        ...rectProperties("source_current", source.currentRect),
      },
      { send_instantly: true, transport: "sendBeacon" }
    );
  };

  const initClsDiagnostics = (environment) => {
    if (!["production", "staging"].includes(environment)) {
      return;
    }

    ensureArrayAt();
    onCLS(captureClsDiagnostic, { reportAllChanges: true });
  };

  let featureFlagsReady = false;
  let hasAnalyticsConsent = false;

  const clearFeatureFlags = () => {
    featureFlagsReady = false;
  };

  // Return the caller's fallback until analytics consent is granted and PostHog
  // reports an error-free feature-flag load. Once ready, getFeatureFlag() records
  // the exposure that PostHog experiments need; the SDK setting below
  // deduplicates it per session.
  const getFeatureFlagVariant = (key, safeFallback) => {
    if (!(hasAnalyticsConsent && featureFlagsReady)) {
      return safeFallback;
    }

    const variant = posthog.getFeatureFlag(key);

    // oxlint-disable-next-line anti-slop/no-runtime-typeof -- This JavaScript URL/DOM/SDK boundary requires a primitive string; coercion and boxed strings change accepted inputs.
    return typeof variant === "string" || typeof variant === "boolean"
      ? variant
      : safeFallback;
  };

  // Subscribe without recording exposure: the caller must first apply the result.
  // null is a resolved failure/disabled flag; unresolved consent/flags keep waiting.
  const watchFeatureFlagVariant = (key, onNotification) => {
    const check = (_flags, _variants, { errorsLoading } = {}) => {
      if (errorsLoading) {
        onNotification(null);
      } else if (hasAnalyticsConsent && featureFlagsReady) {
        onNotification(
          posthog.is_capturing() && posthog.get_distinct_id()
            ? (posthog.getFeatureFlag(key, { send_event: false }) ?? null)
            : null
        );
      }
    };

    const stopFlags = posthog.onFeatureFlags(check);

    const stopConsent = watchCategory(CATEGORIES.ANALYTICS, (allowed) => {
      if (!allowed) {
        onNotification(null);
      }
    });

    check();

    return () => {
      stopFlags();
      stopConsent();
    };
  };

  const captureMemberfulOrderReturn = () => {
    const orderUuid = window.mtnMemberfulOrderUuid;
    clearMemberfulOrderUuid();

    if (
      // oxlint-disable-next-line anti-slop/no-runtime-typeof -- This JavaScript URL/DOM/SDK boundary requires a primitive string; coercion and boxed strings change accepted inputs.
      typeof orderUuid === "string" &&
      MEMBERFUL_ORDER_UUID_PATTERN.test(orderUuid)
    ) {
      posthog.capture("memberful_checkout_returned", {
        memberful_order_uuid: orderUuid.toLowerCase(),
      });
    }
  };

  const drainPendingCaptures = (flush) => {
    const buffered = pendingCaptures;
    pendingCaptures = null;

    if (flush) {
      for (const fn of buffered ?? []) {
        fn();
      }
    }
  };

  // Side effects to apply when Termly transitions analytics consent to granted.
  // Called from the watchCategory handler in the loaded callback above. We
  // identify only logged-in members here — we deliberately do NOT create a person
  // profile for anonymous visitors: a profile would flip every consenting
  // visitor's events to PostHog's identified billing tier (~2-4x the anonymous
  // rate) for no payoff (MTN-707). Members identify by WP user ID with
  // is_member and is_staff only — no email, no hashed email, no mtn_id
  // (MTN-1445); is_staff replaces the email-domain internal-user filter.
  // Exported for unit testing (posthog.test.js); not part of the module's public API.
  const applyOptIn = () => {
    clearFeatureFlags();

    const currentUserId =
      window.mtnUser?.id === undefined ? undefined : String(window.mtnUser.id);

    const persistedUserId = posthog.get_property("$user_id");

    if (persistedUserId && String(persistedUserId) !== currentUserId) {
      posthog.reset();
      clearDistinctIdCookie();
      clearFeatureFlags();
    }

    hasAnalyticsConsent = true;
    // Clear any stale opt-out reason from a prior cookieless session in the
    // same tab — without this, events fired post-upgrade would carry the
    // earlier reason and skew the diagnostic group-by.
    posthog.unregister("mtn_opt_out_reason");

    // Suppress $opt_in re-emission on page-load reapplies; only emit on new grants.
    if (posthog.has_opted_in_capturing()) {
      posthog.opt_in_capturing({ captureEventName: null });
    } else {
      posthog.opt_in_capturing();
    }

    if (window.mtnUser?.id) {
      posthog.identify(String(window.mtnUser.id), {
        is_member: !!window.mtnUser.hasPaidPlan,
        is_staff: !!window.mtnUser.isStaff,
      });
    }

    syncDistinctIdCookie();
    posthog.set_config({ advanced_disable_feature_flags: false });
    posthog.reloadFeatureFlags();
  };

  const init = () => {
    if (initialized) {
      return;
    }

    initialized = true;

    const environment = getEnvironment();

    posthog.init("phc_sF3C2mVidswocgru8rfmPb47k7QLaCTPx3aDzzALRnFN", {
      advanced_disable_feature_flags: true,
      advanced_disable_feature_flags_on_first_load: true,
      advanced_feature_flags_dedup_per_session: true,
      api_host: "https://hg.moneytalksnews.com",
      autocapture: false,
      before_send: beforeSend,
      capture_pageleave: true,
      capture_pageview: "history_change",
      cookieless_mode: "on_reject",
      debug: environment === "development",
      defaults: "2026-01-30",
      disable_session_recording: true,
      feature_flag_cache_ttl_ms: 300_000,
      loaded: () => {
        // Register super properties before wiring the consent watchers: when
        // Termly has already resolved, watchCategory reports synchronously and
        // the buffer flush below would otherwise ship events without
        // environment/release.
        posthog.register({
          environment,
          release: `mtn@${version}`,
        });
        posthog.onFeatureFlags((_flags, _variants, { errorsLoading } = {}) => {
          featureFlagsReady = !errorsLoading;
        });
        onUnavailable(
          (reason) => {
            clearMemberfulOrderUuid();

            if (posthog.get_explicit_consent_status() === "pending") {
              hasAnalyticsConsent = false;
              clearFeatureFlags();
              optOutWithReason(reason);
            }

            drainPendingCaptures(true);
          },
          { timeoutMs: 8000 }
        );
        watchCategory(CATEGORIES.ANALYTICS, (hasAnalytics) => {
          if (hasAnalytics) {
            applyOptIn();
            captureMemberfulOrderReturn();
            drainPendingCaptures(true);
          } else {
            clearMemberfulOrderUuid();
            hasAnalyticsConsent = false;
            clearFeatureFlags();
            optOutWithReason("termly_reject");
            drainPendingCaptures(false);
          }
        });
      },
      person_profiles: "identified_only",
    });

    initClsDiagnostics(environment);
  };

  const recordClsMutation = (type, properties = {}) => {
    const cleanProperties = {};

    for (const [key, value] of Object.entries(properties)) {
      const cleanValue = cleanMarkerValue(value);

      if (cleanValue !== undefined) {
        cleanProperties[key] = cleanValue;
      }
    }

    clsMutationMarks.push({
      page_age_ms: now(),
      type,
      ...cleanProperties,
    });

    if (clsMutationMarks.length > CLS_DIAGNOSTIC_MUTATION_LIMIT) {
      clsMutationMarks.splice(
        0,
        clsMutationMarks.length - CLS_DIAGNOSTIC_MUTATION_LIMIT
      );
    }
  };

  const captureException = (error, properties) =>
    runOrBuffer(() => posthog.captureException(error, properties));

  const captureMessage = (message, properties) => {
    // Construct the Error at call time so a buffered flush keeps the
    // caller's stack instead of the drain loop's.
    const error = new Error(message);
    runOrBuffer(() =>
      posthog.captureException(error, {
        level: "error",
        ...properties,
      })
    );
  };

  const setUser = (userId, properties) =>
    posthog.identify(String(userId), properties);

  return {
    applyOptIn,
    beforeSend,
    capture,
    captureException,
    captureMessage,
    getFeatureFlagVariant,
    init,
    recordClsMutation,
    setUser,
    watchFeatureFlagVariant,
  };
};
